/**
 * #46 fix (2026-05-18): First-time Google sign-up landing page.
 *
 * AuthContext.loginWithGoogle creates the user doc with
 * `termsAcceptedAt: null` for new users. Login/Register route them here
 * instead of /dashboard. This page captures their explicit consent and
 * stamps `termsAcceptedAt` + `privacyAcceptedAt` before letting them in.
 *
 * Required for COPPA + general consent-record best practice — see #124.
 *
 * 2026-09-05 (audit C3 break #1): also captures DATE OF BIRTH. OAuth users
 * never see the email registration form, so without this they would be the one
 * cohort with no age on record — and COPPA cannot be applied to an account
 * whose age is unknown. Same immutability guarantee: written once here, then
 * rejected by `firestore.rules` on any later client write.
 */

import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { validateBirthDate, requiresConsentFlag } from '../../utils/age';

export const AcceptTerms: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  /**
   * S98: pre-filled from the date of birth the child already typed on `Register`.
   *
   * `handleGoogleSignup` did not read that input at all, so a 9-year-old could
   * fill in their real birth date, click "Sign up with Google", and be provisioned
   * with no age — the age they gave was thrown away at the moment it mattered.
   * It is still validated on submit here, and still set-once, so pre-filling
   * changes what the child has to retype and nothing about the guarantees.
   */
  const [birthDate, setBirthDate] = useState(() => {
    // S232: from navigation state (Register puts it there); never from the URL any more.
    const fromState = (location.state as { birthDate?: unknown } | null)?.birthDate;
    const carried = typeof fromState === 'string' ? fromState : '';
    return validateBirthDate(carried) === null ? carried : '';
  });
  const [birthDateError, setBirthDateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!accepted) {
      setError('Please accept the Terms and Privacy Policy to continue.');
      return;
    }
    const dobError = validateBirthDate(birthDate);
    if (dobError) {
      setBirthDateError(dobError);
      return;
    }
    setSubmitting(true);
    setError(null);
    setBirthDateError(null);
    try {
      const requiresParentalConsent = requiresConsentFlag(birthDate);
      await updateDoc(doc(db.value, 'users', currentUser.uid), {
        termsAcceptedAt: serverTimestamp(),
        privacyAcceptedAt: serverTimestamp(),
        termsVersion: '2026-05-17', // bump this whenever PP/ToS materially changes
        birthDate,
        requiresParentalConsent,
      });
      // Under-13 accounts go to the consent handshake, not the product.
      navigate(requiresParentalConsent ? '/consent' : '/dashboard');
    } catch (err) {
      setError((err instanceof Error && err.message) || 'Could not record acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    // Don't keep an un-consented account sitting in the auth pool.
    try {
      await logout();
    } catch {
      /* ignore */
    }
    navigate('/');
  };

  return (
    <div className="auth-container" style={{ padding: '40px 20px' }}>
      <div className="auth-card" style={{ maxWidth: 600, margin: '0 auto' }}>
        <h1>One last step</h1>
        <p>
          Before you start using NS Gaming Studio, please review and accept
          the Terms of Service and Privacy Policy. This is a one-time step.
        </p>

        <div style={{ margin: '24px 0' }}>
          <label htmlFor="acceptTermsBirthDate" style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
            Date of Birth
          </label>
          <input
            id="acceptTermsBirthDate"
            type="date"
            value={birthDate}
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => {
              setBirthDate(e.target.value);
              if (birthDateError) setBirthDateError(null);
            }}
            required
            aria-describedby={birthDateError ? 'acceptTerms-dob-error' : 'acceptTerms-dob-hint'}
            aria-invalid={!!birthDateError}
            style={{ width: '100%', padding: '10px', marginBottom: 6 }}
          />
          <div id="acceptTerms-dob-hint" style={{ fontSize: 12, opacity: 0.75, lineHeight: 1.45 }}>
            We ask so we can protect younger creators. If you're under 13, we'll ask a
            parent for permission before you start.
          </div>
          {birthDateError && (
            <div id="acceptTerms-dob-error" role="alert" style={{ color: '#c00', fontSize: 12, marginTop: 6 }}>
              {birthDateError}
            </div>
          )}
        </div>

        <div style={{ margin: '24px 0' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              style={{ marginTop: 4 }}
            />
            <span>
              I agree to the <Link to="/terms" target="_blank">Terms of Service</Link>{' '}
              and acknowledge the <Link to="/privacy" target="_blank">Privacy Policy</Link>.
            </span>
          </label>
        </div>

        {error && (
          <div role="alert" style={{ color: '#c00', marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!accepted || !birthDate || submitting}
            className="btn btn-primary"
          >
            {submitting ? 'Saving…' : 'Continue'}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting}
            className="btn btn-secondary"
          >
            Decline & sign out
          </button>
        </div>
      </div>
    </div>
  );
};
